import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AccountBalance,
  Box,
  Pagination,
  Skeleton,
  formattedCurrency,
  getCurrencySign,
  useDateLocale,
  withStyles,
} from 'vopay';
import { Button, Table as UITable } from 'UI';
import { usePartnerData } from '@/providers/PartnerData.provider';
import useCountryLabels from '@/hooks/useCountryLabels';
import Table, { ColConfig, createTableProps, Row } from './Table/Table';
import AccountBreadcrumbs from './AccountBreadcrumbs/AccountBreadcrumbs';
import AccountStatus from './AccountStatus/AccountStatus';
import derivePartnerAccountStatus from './partnerAccountStatus';
import { formatApiDate } from './accountStatusDate';
import type { AccountsListing } from './useAccountsListing';
import styles from './AccountsTable.module.scss';

const cn = withStyles(styles);

type AccountsTableProps = {
  listing: AccountsListing;
  /** The Partner Account Name column: which partner in the session's book opened the account. */
  hasPartnerColumn: boolean;
  /** Account Name opens the details drawer (spec 5.3); the full page is a click further, from the drawer. */
  onAccountClick: (account: AccountBalance) => void;
  /** A row's Transfer button; without it the Actions column is not shown. */
  onTransferClick?: (account: AccountBalance) => void;
};

/**
 * The accounts table of a partner's book (spec 5.3), with its paginator: nested sub-account rows at
 * rest, flattened with breadcrumbs while a search is applied. Shared by the Accounts page and the
 * partner page's Accounts tab, so the two never drift.
 */
export default function AccountsTable({
  listing,
  hasPartnerColumn,
  onAccountClick,
  onTransferClick,
}: AccountsTableProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.List' });
  const dateLocale = useDateLocale();
  const { resolveCountryLabel } = useCountryLabels();

  const {
    response: { isRoleViewOnly, accountId: loggedInAccountId },
  } = usePartnerData();

  const {
    balances,
    fetchingAccounts,
    hasLoaded,
    filters: { status },
    hasSearch,
    pageCount,
    pageIndex,
    setPageIndex,
    searchForAccount,
  } = listing;

  const isLoading = fetchingAccounts || !hasLoaded;

  /**
   * earthnode applies the status filter to the top-level rows only — nested sub-accounts arrive from
   * a separate, unfiltered call — so the one filter that keeps the tree (Active) would otherwise show
   * Pending and Disabled sub-accounts underneath Active parents. Status is the only filter this has
   * to answer for: every other one flattens the table, and the sub-rows are never built.
   */
  const matchesStatusFilter = (account: AccountBalance) =>
    status === '' || status === 'all' || account.AccountStatus === status;

  // One mapper. The flat (search) table is the nested one with the sub-rows left off, which is
  // what removes the tree and makes the breadcrumbs the only way back up it.
  function toRows(accounts: AccountBalance[], { nested }: { nested: boolean }): Row<AccountBalance>[] {
    return accounts.map((account) => {
      const subAccounts = (account.SubAccounts || []).filter(matchesStatusFilter);

      return {
        id: `${account.AccountID}-${account.Currency}`,
        data: account,
        subRows: nested && subAccounts.length > 0 ? toRows(subAccounts, { nested }) : undefined,
      };
    });
  }

  const renderPartnerName = ({ data }: { data: AccountBalance }) => {
    if (data.PartnerAccountID && data.PartnerAccountID === loggedInAccountId) {
      return (
        <>
          {data.PartnerAccountName} • {t('Table.direct', { defaultValue: 'Direct' })}
        </>
      );
    }

    return data.PartnerAccountID ? (
      <UITable.Link to={{ pathname: `/partners/${data.PartnerAccountID}` }}>{data.PartnerAccountName}</UITable.Link>
    ) : (
      <>{data.PartnerAccountName}</>
    );
  };

  const renderAccountLink = (data: AccountBalance) => (
    <UITable.LinkButton onClick={() => onAccountClick(data)}>{data.AccountName}</UITable.LinkButton>
  );

  // The nested and the flat (search) tables share every column but Account Name, which carries
  // breadcrumbs in the flat one. Spec 5.3 column order.
  function buildColumns(
    accountName: (row: { data: AccountBalance }) => ReactNode,
  ): ColConfig<AccountBalance, string>[] {
    return [
      {
        id: 'dateAdded',
        header: t('Table.dateAdded', { defaultValue: 'Date Added' }),
        align: 'left',
        template: ({ data }) => <span className={cn('dateCell')}>{formatApiDate(data.DateAdded, dateLocale)}</span>,
      },
      {
        id: 'accountName',
        header: t('Table.accountName', { defaultValue: 'Account Name' }),
        align: 'left',
        template: accountName,
      },
      {
        id: 'partnerAccountName',
        header: t('Table.partnerAccountName', { defaultValue: 'Partner Account Name' }),
        align: 'left',
        visible: hasPartnerColumn,
        template: renderPartnerName,
      },
      {
        id: 'status',
        header: t('Table.status', { defaultValue: 'Status' }),
        align: 'left',
        template: ({ data }) => <AccountStatus account={data} />,
      },
      {
        id: 'country',
        header: t('Table.country', { defaultValue: 'Country' }),
        align: 'left',
        template: ({ data }) => resolveCountryLabel(data.Country),
      },
      {
        id: 'pending',
        header: t('Table.pending', { defaultValue: 'Pending Balance' }),
        align: 'left',
        template: ({ data }) =>
          `${data.Currency} ${getCurrencySign(data.Currency)}${formattedCurrency(data.PendingBalance)}`,
      },
      {
        id: 'available',
        header: t('Table.available', { defaultValue: 'Available Balance' }),
        align: 'left',
        template: ({ data }) =>
          `${data.Currency} ${getCurrencySign(data.Currency)}${formattedCurrency(data.AvailableBalance)}`,
      },
      // Transfers debit an Active account — the status the badge shows, so a Blocked account (still
      // IsActive underneath) gets no button — and only a host that offers transfers shows the column;
      // otherwise it would never hold anything.
      {
        id: 'actions',
        header: t('Table.actions', { defaultValue: 'Actions' }),
        align: 'left',
        visible: onTransferClick !== undefined,
        template: ({ data }) =>
          onTransferClick && derivePartnerAccountStatus(data) === 'Active' ? (
            <Button
              id={`accounts-transfer-${data.AccountID}-${data.Currency}`}
              size="small"
              disabled={isRoleViewOnly}
              onClick={() => onTransferClick(data)}
            >
              {t('transferButton', { defaultValue: 'Transfer' })}
            </Button>
          ) : null,
        hasDivider: false,
      },
    ];
  }

  const tableProps = createTableProps({
    id: 'accountsList',
    hasPlaceholderArrows: true,
    rows: toRows(balances, { nested: true }),
    columns: buildColumns(({ data }) => (
      <div className={cn('accountNameCell')}>
        {renderAccountLink(data)}

        {data.DBAName && (
          <>
            <br />
            <i>{data.DBAName}</i>
          </>
        )}
      </div>
    )),
  });

  const searchTableProps = createTableProps({
    id: 'accountsList',
    hasPlaceholderArrows: false,
    rows: toRows(balances, { nested: false }),
    columns: buildColumns(({ data }) => (
      <Box flx="col" ai="fs">
        <AccountBreadcrumbs
          parentAccounts={data.ParentAccounts}
          onCrumbClick={(parent) => searchForAccount(parent.AccountName)}
        />

        {renderAccountLink(data)}
        {data.DBAName && <i>{data.DBAName}</i>}
      </Box>
    )),
  });

  return (
    <>
      <Box className={cn('accountsTableContainer', 'scrollbar')}>
        {/* The Box is a flex row, so the skeleton's inline container needs a width of its own. */}
        {isLoading && <Skeleton height={320} containerClassName={cn('tableSkeleton')} />}
        {!isLoading && !hasSearch && <Table {...tableProps} />}
        {!isLoading && hasSearch && <Table {...searchTableProps} />}
      </Box>

      <Box flx="row" jc="center" mt="md" base="8">
        {pageCount > 1 && (
          <Pagination
            pageCount={pageCount}
            forcePageNumber={pageIndex}
            handlePageClick={(e) => {
              setPageIndex(e.selected);
            }}
          />
        )}
      </Box>
    </>
  );
}
